import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import RenterProfileRatings from './RenterProfileRatings';
import RefundModal from './RefundModal';
import { invoiceService } from '../../../services/invoiceService';
import { bookingService } from '../../../services/bookingService';
import { emailService } from '../../../services/emailService';


const BookingRequestCard = ({ request }) => {
  const [status, setStatus] = useState(request?.status);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRenterRatings, setShowRenterRatings] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundInfo, setRefundInfo] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleAccept = async () => {
    setIsProcessing(true);
    try {
      await bookingService?.updateBookingStatus(request?.id, 'confirmed');
      setStatus('confirmed');

      // Send email notification to renter
      const renterEmail = request?.renter?.email || request?.renterEmail;
      const renterName = request?.renter?.fullName || request?.renterName;
      if (renterEmail) {
        await emailService?.sendBookingStatusUpdate({
          recipientEmail: renterEmail,
          recipientName: renterName,
          emailType: 'booking_accepted',
          bookingData: {
            bookingReference: request?.bookingReference || request?.id,
            startDate: request?.pickupDate || request?.startDate,
            endDate: request?.returnDate || request?.endDate,
            totalAmount: request?.totalAmount
          },
          trailerData: {
            title: request?.trailerName || request?.trailer?.title,
            type: request?.trailerType || request?.trailer?.trailerType,
            location: request?.location
          }
        });
      }
    } catch (error) {
      console.error('Failed to accept booking:', error);
      alert('Failed to accept booking. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    setIsProcessing(true);
    try {
      await bookingService?.updateBookingStatus(request?.id, 'cancelled');
      setStatus('cancelled');

      // Send email notification to renter
      const renterEmail = request?.renter?.email || request?.renterEmail;
      const renterName = request?.renter?.fullName || request?.renterName;
      if (renterEmail) {
        await emailService?.sendBookingStatusUpdate({
          recipientEmail: renterEmail,
          recipientName: renterName,
          emailType: 'booking_declined',
          bookingData: {
            bookingReference: request?.bookingReference || request?.id,
            startDate: request?.pickupDate || request?.startDate,
            endDate: request?.returnDate || request?.endDate,
            totalAmount: request?.totalAmount
          },
          trailerData: {
            title: request?.trailerName || request?.trailer?.title,
            type: request?.trailerType || request?.trailer?.trailerType,
            location: request?.location
          }
        });
      }
    } catch (error) {
      console.error('Failed to decline booking:', error);
      alert('Failed to decline booking. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      setIsDownloadingInvoice(true);
      await invoiceService?.generatePDF(request?.id || request?.bookingId, {
        title: request?.trailerName,
        type: request?.trailerType,
        location: request?.location
      });
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice. Please try again.');
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  const handleRefundSuccess = ({ bookingId, refundAmount, isFullRefund, message }) => {
    setRefundInfo({ refundAmount, isFullRefund });
    if (isFullRefund) {
      setStatus('cancelled');
    }
    setShowRefundModal(false);
    alert(message || `Refund of $${refundAmount} processed successfully.`);
  };

  const renderRatingStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={12}
            strokeWidth={2}
            className={star <= rating ? 'text-accent fill-accent' : 'text-muted-foreground'}
          />
        ))}
      </div>
    );
  };

  if (status === 'confirmed') {
    return (
      <>
        <div className="bg-success/5 border border-success/20 rounded-lg p-4">
          <div className="flex items-center flex-wrap gap-3">
            <div className="w-10 h-10 bg-success rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="CheckCircle" size={20} color="white" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-body font-medium text-foreground">Booking Accepted</p>
              <p className="font-caption text-sm text-muted-foreground">
                {request?.renterName} • {request?.trailerName}
              </p>
              {refundInfo && (
                <p className="font-caption text-xs text-warning mt-0.5">
                  {refundInfo?.isFullRefund ? 'Fully refunded' : `Partial refund issued: $${refundInfo?.refundAmount}`}
                </p>
              )}
            </div>
            <div className="flex items-center flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                onClick={handleDownloadInvoice}
                loading={isDownloadingInvoice}
              >
                Invoice
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="DollarSign"
                onClick={() => request?.onAddCharge?.(request)}
              >
                Add Charge
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="RotateCcw"
                onClick={() => setShowRefundModal(true)}
                className="text-warning border-warning/40 hover:bg-warning/10"
              >
                Refund
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="AlertTriangle"
                onClick={() => window.location.href = `/dispute-resolution?bookingId=${request?.id || request?.bookingId}&type=owner`}
              >
                File Dispute
              </Button>
            </div>
          </div>
        </div>
        {showRefundModal && (
          <RefundModal
            booking={request}
            onClose={() => setShowRefundModal(false)}
            onRefundSuccess={handleRefundSuccess}
          />
        )}
      </>
    );
  }

  if (status === 'cancelled') {
    return (
      <div className="bg-muted rounded-lg p-4 opacity-60">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-muted-foreground rounded-full flex items-center justify-center">
            <Icon name="XCircle" size={20} color="white" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <p className="font-body font-medium text-foreground">Booking Declined</p>
            <p className="font-caption text-sm text-muted-foreground">
              {request?.renterName} • {request?.trailerName}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-lg shadow-elevation-2 p-4 hover:shadow-elevation-3 transition-smooth">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Renter Info */}
          <div className="flex items-start space-x-4 flex-1">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={24} color="white" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="font-body font-semibold text-foreground">{request?.renterName}</h3>
                {request?.renterVerified && (
                  <div className="flex items-center space-x-1 px-2 py-0.5 bg-success/10 rounded-full">
                    <Icon name="ShieldCheck" size={12} className="text-success" strokeWidth={2} />
                    <span className="font-caption text-xs text-success">Verified</span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2 mb-2">
                {renderRatingStars(request?.renterRating)}
                <span className="font-caption text-xs text-muted-foreground">
                  {request?.renterRating} rating
                </span>
                <button
                  onClick={() => setShowRenterRatings(!showRenterRatings)}
                  className="text-primary hover:text-primary/80 font-caption text-xs font-medium transition-smooth"
                >
                  {showRenterRatings ? 'Hide' : 'View'} Reviews
                </button>
              </div>
              <p className="font-body text-sm text-foreground font-medium mb-1">{request?.trailerName}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={14} strokeWidth={2} />
                  <span className="font-body">{formatDate(request?.pickupDate)}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="ArrowRight" size={14} strokeWidth={2} />
                  <span className="font-body">{formatDate(request?.returnDate)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Amount and Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 lg:ml-4">
            <div className="text-left sm:text-right">
              <p className="font-caption text-xs text-muted-foreground mb-1">Total Amount</p>
              <p className="font-heading text-2xl font-bold text-primary">${request?.totalAmount}</p>
              <p className="font-caption text-xs text-muted-foreground mt-1">
                Requested {formatDate(request?.requestDate)}
              </p>
            </div>
          </div>
        </div>

        {/* Renter Ratings Section */}
        {showRenterRatings && request?.renterId && (
          <div className="mt-4 pt-4 border-t border-border">
            <RenterProfileRatings renterId={request?.renterId} compact={true} />
          </div>
        )}
      </div>
      {showRefundModal && (
        <RefundModal
          booking={request}
          onClose={() => setShowRefundModal(false)}
          onRefundSuccess={handleRefundSuccess}
        />
      )}
    </>
  );
};

export default BookingRequestCard;